/**
 * SedVsContextDiffTab — SED_Amendment_Persistence SA7.
 *
 * Side-by-side reconciliation surface for every bound claim/SED-path
 * pair. Lives inside the Site Evidence panel below the variable
 * browser; renders nothing when the site has no Site Context (so it's
 * cleanly additive on sites that pre-date SC*).
 *
 * Wire-up contract:
 *   - Reads `site.siteContext` (Site_Context) and the in-memory `sed`
 *     loaded by the parent SiteEvidenceTab.
 *   - Builds the row model via `buildSedContextDiffRows`. The row's
 *     `resolution` field is sourced from `claim.syncHistory` and is
 *     preserved across SED rebuild through the UD framework — the
 *     parent passes a `priorRows` ref so the merge happens at the
 *     component boundary.
 *   - User actions ("Adopt SED value" / "Adopt Site Context value")
 *     bubble through two host-supplied callbacks. The component itself
 *     never writes to Firestore — it's a pure rendering + decision
 *     surface so SA Wave 1's call sites can target it deterministically.
 *
 * data-testid contract (consumed by SA Wave 1 and SA8 tests):
 *   - sed-vs-context-diff-tab           : root container
 *   - sed-vs-context-diff-header-chip   : "N in sync · K diverged" pill
 *   - sed-vs-context-diff-filter-all    : filter toggle "All"
 *   - sed-vs-context-diff-filter-diverged
 *   - sed-vs-context-diff-filter-synced
 *   - sed-vs-context-diff-row-{identity}: per-row container
 *   - sed-vs-context-diff-row-status-{identity}: chip "IN SYNC" / "DIVERGED"
 *   - sed-vs-context-diff-adopt-sed-{identity}     : "Adopt SED value" button
 *   - sed-vs-context-diff-adopt-context-{identity} : "Adopt Site Context value"
 *   - sed-vs-context-diff-footer-breadcrumb        : footer string for reports
 */
import React, { useMemo, useRef, useState } from 'react';
import { ArrowLeftRight, Check, AlertTriangle, Database, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { mergePreservedUserDecisions } from '../../lib/preservedUserDecisions';
import {
  isRowResolved,
  type SedContextDiffRow,
} from '../../services/siteContext/syncSedContextBindings';
import { buildPresentedSedContextDiffRows, presentedDiffStatus, isPresentedOpenDivergence, type PresentedSedContextDiffRow, type PresentedDiffStatus } from '../../services/siteContext/sedContextDiffPresentation';
import type { SiteEntity } from '../../types/site';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';

export interface SedVsContextDiffTabProps {
  site: SiteEntity;
  sed: SiteEvidenceDocument | null;
  /** Optional exact SED field paths (registry or variable-browser shape).
   * A section path includes its subtree; [] includes nothing. */
  scopedFieldPaths?: readonly string[];
  /** Optional section boundary, intersected with scopedFieldPaths when both
   * are supplied. Omitted scope preserves the full reconciliation surface. */
  scopedSectionIds?: readonly string[];
  /** Host wires this to the reverse sync helper. Receives the diff row. */
  onAdoptContext?: (row: SedContextDiffRow) => Promise<void> | void;
  /** Host wires this to the forward sync helper. Receives the diff row. */
  onAdoptSed?: (row: SedContextDiffRow) => Promise<void> | void;
  /**
   * SCE-3 — host wires this to `store.markClaimMisExtracted`. When the
   * user clicks "Mark as mis-extracted" on a diverged row, the claim's
   * `state` flips to `'mis-extracted'` and every read-side surface
   * (this diff tab, the cross-checker, the prompt block) filters it out.
   * The row stays in `siteContext.claims[]` for audit + undo.
   *
   * Optional — when omitted the button does not render so legacy hosts
   * that don't yet wire SCE keep the original two-button layout.
   *
   * The handler receives the row's `claimId`. Diverged rows always carry
   * a claimId; sed-only / context-only rows are skipped at the call site.
   */
  onMarkMisExtracted?: (claimId: string) => Promise<void> | void;
}

/** Scope display rows only; persisted identities and values stay canonical.
 * Registry paths and variable-browser paths differ only by the data wrapper.
 * A section-only path scopes its subtree. Explicit empty arrays match nothing.
 */
function pathMatchesTopicScope(
  path: string,
  fieldPaths?: readonly string[],
  sectionIds?: readonly string[],
): boolean {
  const normalize = (value: string) => value.trim().replace(/^([^.]+)\.data\./, '$1.');
  const normalized = normalize(path);
  const sectionId = normalized.split('.')[0];
  if (sectionIds != null && !sectionIds.some(id => id.trim() === sectionId)) return false;
  if (fieldPaths == null) return true;
  return fieldPaths.some(value => {
    const scope = normalize(value);
    return scope.length > 0 && (normalized === scope || normalized.startsWith(`${scope}.`));
  });
}

type FilterMode = 'all' | 'diverged' | 'synced';

function renderValue(v: SedContextDiffRow['sedValue']): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') {
    // Show enough precision for binding-boundary scalars without
    // turning small integers into "1.000000".
    return Number.isInteger(v) ? String(v) : v.toFixed(3).replace(/\.?0+$/, '');
  }
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}

function statusChipClasses(status: PresentedDiffStatus): string {
  switch (status) {
    case 'in-sync':
      // A resolved-but-still-value-diverged row reads as settled — reuse
      // the success styling so it never flags as an open (red) conflict.
      case 'resolved':
      return 'border-success/40 text-success bg-success/5';
    case 'diverged':
      return 'border-danger/40 text-danger bg-danger/5';
    case 'rounded':
    case 'not-comparable':
    case 'sed-only':
      return 'border-border text-text-secondary';
    case 'context-only':
      return 'border-border text-text-secondary';
  }
}

function statusLabel(status: PresentedDiffStatus): string {
  switch (status) {
    case 'rounded': return 'Same at stated precision';
    case 'not-comparable': return 'Review field binding';
    case 'in-sync': return 'In sync';
    case 'resolved': return 'Resolved';
    case 'diverged': return 'Diverged';
    case 'sed-only': return 'SED only';
    case 'context-only': return 'Context only';
  }
}

export function SedVsContextDiffTab({
  site,
  sed,
  scopedFieldPaths,
  scopedSectionIds,
  onAdoptContext,
  onAdoptSed,
  onMarkMisExtracted,
}: SedVsContextDiffTabProps) {
  const [filter, setFilter] = useState<FilterMode>('all');
  const [busyIdentity, setBusyIdentity] = useState<string | null>(null);

  // Build the rows from current state.
  const freshRows = useMemo<PresentedSedContextDiffRow[]>(
    () => buildPresentedSedContextDiffRows({ siteContext: site.siteContext, sed }),
    [site.siteContext, sed],
  );

  // UD preservation: prior-render rows survive a SED rebuild because
  // mergePreservedUserDecisions overlays {resolution, resolvedAt,
  // resolvedBy} from the prior render onto the fresh row. Identity is
  // `${claimId}::${sedPath}` per the spec.
  //
  // We keep the prior list in a ref so a fresh React tree mount (e.g.
  // after the parent re-instantiates this component on tab switch)
  // still picks up the persisted overlay from claim.syncHistory — the
  // ref is just an in-session cache.
  const priorRowsRef = useRef<PresentedSedContextDiffRow[] | undefined>(undefined);

  const allRows = useMemo<PresentedSedContextDiffRow[]>(() => {
    // SedContextDiffRow has explicit fields rather than an index
    // signature; the UD helper's generic constraint
    // `Record<string, unknown>` accepts the row at runtime — we cast
    // through `unknown` to satisfy TS at the boundary without
    // weakening the row type for the rest of the file.
    type RowAsRecord = PresentedSedContextDiffRow & Record<string, unknown>;
    const [merged] = mergePreservedUserDecisions<RowAsRecord>({
      prior: priorRowsRef.current as unknown as readonly RowAsRecord[] | undefined,
      fresh: freshRows as unknown as readonly RowAsRecord[],
      identity: (item) => item.identity,
      preserveFields: ['resolution', 'resolvedAt', 'resolvedBy'] as const,
    });
    priorRowsRef.current = merged as unknown as PresentedSedContextDiffRow[];
    return merged as unknown as PresentedSedContextDiffRow[];
  }, [freshRows]);

  // Keep decision-preservation over the complete canonical rows. Scope only
  // the presentation and its counts/actions, never the persisted source.
  const rows = useMemo(() => allRows.filter(row => pathMatchesTopicScope(
    row.sedPath, scopedFieldPaths, scopedSectionIds,
  )), [allRows, scopedFieldPaths, scopedSectionIds]);

  const counts = useMemo(() => {
    let inSync = 0;
    let diverged = 0;
    let resolved = 0;
    let rounded = 0;
    let bindingReview = 0;
    for (const r of rows) {
      if (r.comparison === 'rounded') rounded += 1;
      else if (r.comparison === 'not-comparable') bindingReview += 1;
      else if (r.status === 'in-sync') inSync += 1;
      // Only OPEN divergences count as "diverged"; a row the user has
      // resolved (but whose values still differ pending a rebuild) is a
      // settled "resolved" row, not an open conflict.
      else if (isPresentedOpenDivergence(r)) diverged += 1;
      else if (r.status === 'diverged') resolved += 1;
    }
    return { inSync, diverged, resolved, rounded, bindingReview, total: rows.length };
  }, [rows]);

  const visibleRows = useMemo(() => {
    if (filter === 'all') return rows;
    // DIVERGED shows only still-open divergences. SYNCED shows every
    // settled row — genuinely in-sync rows AND resolved divergences.
    if (filter === 'diverged') return rows.filter(r => isPresentedOpenDivergence(r) || r.comparison === 'not-comparable');
    return rows.filter(r => r.comparison === 'rounded' || (r.comparison === 'canonical' && (r.status === 'in-sync' || isRowResolved(r))));
  }, [filter, rows]);

  // No-op render path — keeps the surface dormant for sites without
  // any Site Context.
  if (!site.siteContext || (site.siteContext.claims?.length ?? 0) === 0
    || ((scopedFieldPaths != null || scopedSectionIds != null) && rows.length === 0)) {
    return null;
  }

  const filterBtnClasses = (active: boolean) =>
    `type-body font-mono uppercase tracking-widest px-2 py-1 border rounded-md ${
      active
        ? 'selected-accent'
        : 'border-border text-text-muted hover:text-text-secondary'
    }`;

  return (
    <div
      data-testid="sed-vs-context-diff-tab"
      className="border border-border-subtle p-4 space-y-3"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <ArrowLeftRight className="w-3.5 h-3.5 text-text-secondary" />
          <h3 className="type-section text-text-primary">
            SED vs Site Context
          </h3>
          <span
            data-testid="sed-vs-context-diff-header-chip"
            className="type-body font-mono uppercase tracking-widest px-1.5 py-0.5 border border-border text-text-secondary"
          >
            {counts.inSync} in sync · {counts.diverged} diverged
            {counts.resolved > 0 ? ` · ${counts.resolved} resolved` : ''}
            {counts.rounded > 0 ? ` · ${counts.rounded} same at stated precision` : ''}
            {counts.bindingReview > 0 ? ` · ${counts.bindingReview} field binding${counts.bindingReview === 1 ? '' : 's'} to review` : ''}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            data-testid="sed-vs-context-diff-filter-all"
            onClick={() => setFilter('all')}
            className={filterBtnClasses(filter === 'all')}
          >
            All
          </button>
          <button
            type="button"
            data-testid="sed-vs-context-diff-filter-diverged"
            onClick={() => setFilter('diverged')}
            className={filterBtnClasses(filter === 'diverged')}
          >
            Diverged
          </button>
          <button
            type="button"
            data-testid="sed-vs-context-diff-filter-synced"
            onClick={() => setFilter('synced')}
            className={filterBtnClasses(filter === 'synced')}
          >
            Synced
          </button>
        </div>
      </div>

      {visibleRows.length === 0 && (
        <div className="type-body text-text-muted italic px-1">
          {filter === 'diverged'
            ? 'No open divergences — every bound claim is either in sync or resolved.'
            : filter === 'synced'
              ? 'No settled rows yet — nothing in sync or resolved.'
              : 'No bound claims have a value on either side.'}
        </div>
      )}

      <div className="space-y-2">
        {visibleRows.map(row => (
          <div
            key={row.identity}
            data-testid={`sed-vs-context-diff-row-${row.identity}`}
            className="border border-border-subtle p-2 grid grid-cols-12 gap-2 items-center text-[10px]"
          >
            <div className="col-span-3 space-y-0.5">
              <div className="font-bold text-text-primary">{row.label}</div>
              <div className="type-meta font-mono uppercase tracking-widest text-text-muted">
                {row.sedPath}
              </div>
              {row.claimCategory && (
                <div className="type-meta font-mono uppercase tracking-widest text-text-muted">
                  {row.claimCategory}
                </div>
              )}
              {(row.collapsedClaimCount ?? 0) > 1 && (
                <div className="type-meta text-text-muted italic">
                  {row.collapsedClaimCount} context claims — showing most recent
                </div>
              )}
            </div>
            <div className="col-span-3 flex items-start gap-1.5">
              <Database className="w-3 h-3 mt-0.5 text-text-muted flex-shrink-0" />
              <div>
                <div className="type-meta uppercase tracking-widest text-text-muted">SED</div>
                <div className="font-mono text-text-secondary break-all">{renderValue(row.sedValue)}</div>
              </div>
            </div>
            <div className="col-span-3 flex items-start gap-1.5">
              <FileText className="w-3 h-3 mt-0.5 text-text-muted flex-shrink-0" />
              <div>
                <div className="type-meta uppercase tracking-widest text-text-muted">Site Context</div>
                <div className="font-mono text-text-secondary break-all">{renderValue(row.contextValue)}</div>
              </div>
            </div>
            <div className="col-span-3 flex flex-col items-end gap-1">
              {(() => {
                const effStatus = presentedDiffStatus(row);
                return (
                  <span
                    data-testid={`sed-vs-context-diff-row-status-${row.identity}`}
                    className={`type-meta font-mono uppercase tracking-widest px-1.5 py-0.5 border ${statusChipClasses(effStatus)}`}
                  >
                    {(effStatus === 'in-sync' || effStatus === 'resolved') && <Check className="inline w-2.5 h-2.5 mr-0.5" />}
                    {effStatus === 'diverged' && <AlertTriangle className="inline w-2.5 h-2.5 mr-0.5" />}
                    {statusLabel(effStatus)}
                  </span>
                );
              })()}
              {row.comparisonNote && <p className="type-meta text-text-secondary text-right">{row.comparisonNote}</p>}
              {row.comparison === 'not-comparable' && onMarkMisExtracted && row.claimId && (
                <Button size="sm" variant="outline" className="h-6 type-meta px-2"
                  data-testid={`sed-vs-context-diff-mis-extracted-${row.identity}`} disabled={busyIdentity === row.identity}
                  onClick={async () => { if (!row.claimId) return; setBusyIdentity(row.identity); try { await onMarkMisExtracted(row.claimId); } finally { setBusyIdentity(null); } }}>
                  Mark as mis-extracted
                </Button>
              )}
              {isPresentedOpenDivergence(row) && (
                <div className="flex flex-wrap gap-1 justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 type-meta uppercase tracking-widest px-2"
                    data-testid={`sed-vs-context-diff-adopt-sed-${row.identity}`}
                    disabled={busyIdentity === row.identity || !onAdoptSed}
                    onClick={async () => {
                      if (!onAdoptSed) return;
                      setBusyIdentity(row.identity);
                      try { await onAdoptSed(row); } finally { setBusyIdentity(null); }
                    }}
                  >
                    Adopt SED
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 type-meta uppercase tracking-widest px-2"
                    data-testid={`sed-vs-context-diff-adopt-context-${row.identity}`}
                    disabled={busyIdentity === row.identity || !onAdoptContext}
                    onClick={async () => {
                      if (!onAdoptContext) return;
                      setBusyIdentity(row.identity);
                      try { await onAdoptContext(row); } finally { setBusyIdentity(null); }
                    }}
                  >
                    Adopt Context
                  </Button>
                  {onMarkMisExtracted && row.claimId && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 type-meta uppercase tracking-widest px-2 border-warning/50 text-warning hover:bg-warning/10"
                      title="The claim's value looks type-wrong (e.g. an MW figure pulled into a km slot). This will hide the claim from the diff + the LLM prompt but keep it in claims[] for audit."
                      data-testid={`sed-vs-context-diff-mis-extracted-${row.identity}`}
                      disabled={busyIdentity === row.identity}
                      onClick={async () => {
                        if (!onMarkMisExtracted || !row.claimId) return;
                        setBusyIdentity(row.identity);
                        try { await onMarkMisExtracted(row.claimId); } finally { setBusyIdentity(null); }
                      }}
                    >
                      Mark as mis-extracted
                    </Button>
                  )}
                </div>
              )}
              {row.comparison === 'canonical' && row.status === 'context-only' && row.claimId && (
                <div className="flex flex-wrap gap-1 justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 type-meta uppercase tracking-widest px-2"
                    data-testid={`sed-vs-context-diff-adopt-context-${row.identity}`}
                    disabled={busyIdentity === row.identity || !onAdoptContext}
                    onClick={async () => {
                      if (!onAdoptContext) return;
                      setBusyIdentity(row.identity);
                      try { await onAdoptContext(row); } finally { setBusyIdentity(null); }
                    }}
                  >
                    Adopt Context
                  </Button>
                </div>
              )}
              {row.comparison !== 'canonical' && isRowResolved(row) && (
                <span className="type-meta text-text-muted">Previous choice retained; this comparison still needs review.</span>
              )}
              {row.comparison === 'canonical' && isRowResolved(row) && (
                <span className="type-meta uppercase tracking-widest text-text-muted">
                  {row.resolution === 'adopt-sed' ? 'Resolved: kept SED' : 'Resolved: kept context'}
                  {row.resolvedAt ? ` · ${new Date(row.resolvedAt).toLocaleDateString()}` : ''}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div
        data-testid="sed-vs-context-diff-footer-breadcrumb"
        className="pt-2 border-t border-border-subtle type-meta uppercase tracking-widest text-text-muted"
      >
        Reconciliation surface · {counts.total} bound row{counts.total === 1 ? '' : 's'} · resolutions persist across SED rebuild
      </div>
    </div>
  );
}
